
import React, { useRef, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { getFAQsByTags, FAQItem } from '@/data/faqs';

interface FAQProps {
  tags?: string[];
  title?: string;
  subtitle?: string;
  className?: string;
  variant?: 'default' | 'automatic-sourcing';
}

const FAQ: React.FC<FAQProps> = ({ 
  tags = ["general"], 
  title = "Frequently Asked Questions",
  subtitle = "Common questions about hiring with Cutshort",
  className = "",
  variant = 'default'
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const faqItems: FAQItem[] = getFAQsByTags(tags);

  const faqSchema = useMemo(() => {
    const stringAnswerItems = faqItems.filter(
      (item): item is FAQItem & { answer: string } => typeof item.answer === 'string'
    );
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": stringAnswerItems.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    };
  }, [faqItems]);

  const sectionClass = variant === 'automatic-sourcing' 
    ? `section-padding bg-white ${className}`
    : `py-20 bg-white opacity-0 transition-opacity duration-1000 ${className}`;
  
  const containerClass = variant === 'automatic-sourcing'
    ? 'container-custom'
    : 'container mx-auto px-4 md:px-6';
  
  const contentMaxWidth = variant === 'automatic-sourcing'
    ? 'max-w-4xl'
    : 'max-w-3xl';
  
  const accordionWrapperClass = variant === 'automatic-sourcing'
    ? `${contentMaxWidth} mx-auto`
    : `${contentMaxWidth} mx-auto glass-panel`;
  
  const accordionItemClass = variant === 'automatic-sourcing'
    ? 'border-b border-brand-offwhite'
    : '';
  
  const accordionTriggerClass = variant === 'automatic-sourcing'
    ? 'text-left text-lg font-medium text-brand-darkgrey px-6 py-5 hover:no-underline hover:text-brand-blue transition-colors'
    : 'text-lg font-medium text-brand-darkgrey px-6 py-4';
  
  const accordionContentClass = variant === 'automatic-sourcing'
    ? 'px-6 pb-6 text-base text-brand-grey leading-relaxed'
    : 'px-6 pb-4 text-brand-grey';

  return (
    <section 
      id="faq"
      ref={sectionRef}
      className={sectionClass}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <div className={containerClass}>
        <div className={`${contentMaxWidth} mx-auto ${variant === 'automatic-sourcing' ? 'mb-16' : 'mb-16'} text-center`}>
          {variant === 'default' && (
            <div className="inline-block px-3 py-1 bg-brand-blue/10 rounded-full mb-3">
              <span className="text-sm font-medium text-brand-blue">FAQ</span>
            </div>
          )}
          <h2 className={`text-3xl md:text-4xl font-bold text-brand-darkgrey ${variant === 'automatic-sourcing' ? 'mb-4' : 'mb-6'}`}>
            {title}
          </h2>
          {variant === 'automatic-sourcing' && (
            <div className="h-1 w-16 bg-brand-yellow mx-auto rounded-full mb-4" />
          )}
          <p className="text-lg text-brand-grey">
            {subtitle}
          </p>
        </div>
        
        <div className={accordionWrapperClass}>
          <Accordion type="single" collapsible className={`w-full ${variant === 'automatic-sourcing' ? 'border-t border-brand-offwhite' : 'text-left'}`}>
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className={accordionItemClass}>
                <AccordionTrigger className={accordionTriggerClass}>
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className={accordionContentClass}>
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
